import { Suspense } from "react";
import "./App.css";
import PageTitle from "./components/PageTitle";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import "react-phone-input-2/lib/style.css";
import "react-quill/dist/quill.snow.css";
import "react-datepicker/dist/react-datepicker.css";
import "./scss/main.scss";
import configureStoreProd from "./config/configureStore.prod";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { ToastContainer } from "react-toastify";
import { lazy } from "react";

const { persistor, store } = configureStoreProd();

function App() {
  const routes = [
    { url: "/", element: <Navigate to="/login" /> },
    { url: "*", path: "NotFoundPage" }, // Not Found Page
    { url: "/login", path: "Login" }, // Auth Page Routes
    { url: "/forgotpassword", path: "Forgotpassword" },
    { url: "/reset-password", path: "Resetpassword" },
    { url: "/dashboard", path: "Dashboard" }, // Dashboard Route
    { url: "/notifications", path: "Notifications" }, // Notifications admin
    { url: "/profile", path: "Profile" }, // Profile Page Routes
    { url: "/profile/:tab", path: "Settings" },
    { url: "/user", path: "Users" }, // Staff Module Routes
    { url: "/user/edit/:id", path: "Users/AddEdit" },
    { url: "/user/add", path: "Users/AddEdit" },
    { url: "/user/detail/:id", path: "Users/View" },
    { url: "/user-report", path: "UserReport" }, // Staff Module Routes
    { url: "/user-report/edit/:id", path: "UserReport/AddEdit" },
    { url: "/user-report/add", path: "UserReport/AddEdit" },
    { url: "/user-report/detail/:id", path: "UserReport/View" },
    { url: "/admin/experience", path: "Experience" },
    { url: "/admin/experience/retours", path: "Experience" },
    { url: "/admin/experience/retours/:id", path: "Experience" },
    { url: "/admin/experience/avis-publics", path: "Experience" },
    { url: "/admin/experience/avis-publics/:id", path: "Experience" },
    { url: "/admin/experience/campagnes", path: "Experience" },
    { url: "/admin/experience/campagnes/:id", path: "Experience" },
    { url: "/admin/experience/cas-usage", path: "Experience" },
    { url: "/admin/experience/cas-usage/:id", path: "Experience" },
    { url: "/admin/experience/parametres", path: "Experience" },
    { url: "/admin/experience/audit", path: "Experience" },
    { url: "/admin/social", path: "Social" },
    { url: "/admin/social/studio", path: "Social" },
    { url: "/admin/social/automatique", path: "Social" },
    { url: "/admin/social/editeur", path: "Social" },
    { url: "/admin/social/a-venir", path: "Social" },
    { url: "/admin/social/historique", path: "Social" },
    { url: "/admin/social/ia", path: "Social" },
    { url: "/admin/social/supervision", path: "Social" },
    { url: "/admin/social/parametres", path: "Social" },
    { url: "/invitations", path: "Invitations" },
    { url: "/onboarding", path: "Onboarding" },
    { url: "/onboarding/detail/:id", path: "Onboarding/View" },
    { url: "/score/users", path: "ScoreUsers" },
    { url: "/score/users/detail/:id", path: "ScoreUsers/View" },
    { url: "/score/interests", path: "ScoreInterests" },
    { url: "/score/interests/detail/:id", path: "ScoreInterests/View" },
    { url: "/score/parameters", path: "ScoreParameters" },
    { url: "/confidence/users", path: "ConfidenceUsers" },
    { url: "/confidence/users/detail/:id", path: "ConfidenceUsers/View" },
    { url: "/confidence/leads", path: "ConfidenceLeads" },
    { url: "/confidence/leads/detail/:id", path: "ConfidenceLeads/View" },

    { url: "/company", path: "Agency" }, // Staff Module Routes
    { url: "/company-dormant", path: "DormantAccounts" },
    { url: "/company-invitations", path: "ProInvitations" },
    { url: "/pro-type", path: "ProType" },
    { url: "/pro-subfamily", path: "ProSubFamily" },
    { url: "/company/edit/:id", path: "Agency/AddEdit" },
    { url: "/company/add", path: "Agency/AddEdit" },
    { url: "/company/detail/:id", path: "Agency/View" },
    { url: "/marketplace", path: "Marketplace" },
    { url: "/marketplace/services", path: "Marketplace/Services" },
    { url: "/marketplace/service-models/add", path: "Marketplace/ServiceModelAddEdit" },
    { url: "/marketplace/service-models/edit/:id", path: "Marketplace/ServiceModelAddEdit" },
    { url: "/marketplace/partners", path: "Marketplace/Partners" },
    { url: "/marketplace/partners/:id", path: "Marketplace/PartnerDetail" },
    { url: "/marketplace/offered-services", path: "Marketplace/OfferedServices" },
    { url: "/marketplace/transactions", path: "Marketplace/Transactions" },
    { url: "/marketplace/transactions/:id", path: "Marketplace/TransactionDetail" },
    { url: "/marketplace/litigations", path: "Marketplace/Litigations" },
    { url: "/marketplace/litigations/:id", path: "Marketplace/LitigationDetail" },
    { url: "/marketplace/requests", path: "Marketplace/Requests" },
    { url: "/marketplace/requests/:id", path: "Marketplace/RequestDetail" },
    { url: "/marketplace/settings", path: "Marketplace/Settings" },
    { url: "/data", element: <Navigate to="/data/import-transactions" /> },
    { url: "/data/import-transactions", path: "TransactionsImport" },
    { url: "/bizdev-leads", path: "BizDevLeads" },
    { url: "/parcours", path: "Parcours/Index" }, // Parcours immo digital
    { url: "/parcours/:screen", path: "Parcours/Index" },
    { url: "/staff", path: "Staff" }, // Staff Module Routes
    { url: "/staff/edit/:id", path: "Staff/AddEdit" },
    { url: "/staff/add", path: "Staff/AddEdit" },
    { url: "/staff/detail/:id", path: "Staff/View" },
    { url: "/accounts", path: "Accounts" }, // Accounts Module (admin + staff)
    { url: "/accounts/edit/:id", path: "Accounts/AddEdit" },
    { url: "/accounts/add", path: "Accounts/AddEdit" },
    { url: "/accounts/detail/:id", path: "Accounts/View" },
    { url: "/blog-category", path: "Category" }, // Category Module Routes
    { url: "/blog-category/edit/:id", path: "Category/AddEdit" },
    { url: "/blog-category/add", path: "Category/AddEdit" },
    { url: "/blog-category/detail/:id", path: "Category/View" },
    { url: "/blog-category-type", path: "CategoryType" }, // Staff Module Routes
    { url: "/blog-category-type/edit/:id", path: "CategoryType/AddEdit" },
    { url: "/blog-category-type/add", path: "CategoryType/AddEdit" },
    { url: "/blog-category-type/detail/:id", path: "CategoryType/View" },
    { url: "/category-form", path: "CategoryForm" }, // Category Form Module Routes
    { url: "/category-form/detail/:id", path: "CategoryForm/View" },
    { url: "/amenities", path: "Amenities" }, // Amenities Module Routes
    { url: "/amenities/edit/:id", path: "Amenities/AddEdit" },
    { url: "/amenities/add", path: "Amenities/AddEdit" },
    { url: "/amenities/detail/:id", path: "Amenities/View" },
    { url: "/venues", path: "Venues" }, // Venues Module Routes
    { url: "/venues/edit/:id", path: "Venues/AddEdit" },
    { url: "/venues/add", path: "Venues/AddEdit" },
    { url: "/venues/detail/:id", path: "Venues/View" },
    { url: "/blog", path: "Blog" }, // Blog Module Routes
    { url: "/blog/edit/:id", path: "Blog/AddEdit" },
    { url: "/blog/add", path: "Blog/AddEdit" },
    { url: "/blog/detail/:id", path: "Blog/View" },
    { url: "/contentmanagement", path: "Contentmanagement" }, // contentmanagement Module Routes
    { url: "/contentmanagement/edit/:id", path: "Contentmanagement/AddEdit" },
    { url: "/contentmanagement/add", path: "Contentmanagement/AddEdit" },
    { url: "/contentmanagement/detail/:id", path: "Contentmanagement/View" },
    { url: "/property", path: "Property" }, // Property Module Routes
    { url: "/property/edit/:id", path: "Property/AddEdit" },
    { url: "/property/add", path: "Property/AddEdit" },
    { url: "/property/add/:step", path: "Property/AddEdit" },
    { url: "/property/add/:id/:step", path: "Property/AddEdit" },
    { url: "/property/:slug", path: "Property/AddEdit" },
    { url: "/property/detail/:id", path: "Property/View" },
    { url: "/property/admin/:id", path: "Property/AdminView" },
    { url: "/property-requests", path: "PropertyRequests" },
    { url: "/company/admin/:id", path: "Agency/AdminView" },

    { url: "/schoolproperty", path: "SchoolProperty" }, // School Property Module Routes
    { url: "/schoolproperty/edit/:id", path: "SchoolProperty/AddEdit" },
    { url: "/schoolproperty/add", path: "SchoolProperty/AddEdit" },
    { url: "/schoolproperty/detail/:id", path: "SchoolProperty/View" },
    { url: "/school-types", path: "SchoolTypes" }, // School Types Module Routes
    { url: "/school-types/add", path: "SchoolTypes/AddEdit" },
    { url: "/school-types/edit/:id", path: "SchoolTypes/AddEdit" },

    { url: "/verification", path: "DocumentVerification" }, // User verification(Declarative rating) Module Routes
    { url: "/verification/edit/:id", path: "DocumentVerification/AddEdit" },
    { url: "/verification/add", path: "DocumentVerification/AddEdit" },
    { url: "/verification/detail/:id", path: "DocumentVerification/View" },

    { url: "/funnelvideo/add", path: "FunnelVideo/AddEdit" },
    { url: "/funnelvideo/edit/:id", path: "FunnelVideo/AddEdit" },
    { url: "/funnelvideo", path: "FunnelVideo" },
    { url: "/funnelvideo/detail/:id", path: "FunnelVideo/View" },

    { url: "/persona", path: "Persona" },
    { url: "/persona/add", path: "Persona/AddEdit" },
    { url: "/persona/edit/:id", path: "Persona/AddEdit" },
    { url: "/persona/detail/:id", path: "Persona/View" },

    { url: "/property-revenue", path: "Revenue" },
    { url: "/property-revenue/add", path: "Revenue/AddEdit" },
    { url: "/property-revenue/edit/:id", path: "Revenue/AddEdit" },
    { url: "/property-revenue/detail/:id", path: "Revenue/View" },
    { url: "/property-revenue-source", path: "RevenueSource" },
    { url: "/property-revenue-source/add", path: "RevenueSource/AddEdit" },
    { url: "/property-revenue-source/edit/:id", path: "RevenueSource/AddEdit" },
    { url: "/property-revenue-source/detail/:id", path: "RevenueSource/View" },
    { url: "/property-state", path: "PropertyState" },
    { url: "/property-state/add", path: "PropertyState/AddEdit" },
    { url: "/property-state/edit/:id", path: "PropertyState/AddEdit" },
    { url: "/property-state/detail/:id", path: "PropertyState/View" },
    { url: "/property-expense", path: "Expense" },
    { url: "/property-expense/add", path: "Expense/AddEdit" },
    { url: "/property-expense/edit/:id", path: "Expense/AddEdit" },
    { url: "/property-expense/detail/:id", path: "Expense/View" },
    { url: "/property-renovation", path: "Renovation" },
    { url: "/property-renovation/add", path: "Renovation/AddEdit" },
    { url: "/property-renovation/edit/:id", path: "Renovation/AddEdit" },
    { url: "/property-renovation/detail/:id", path: "Renovation/View" },
    { url: "/property-ratings", path: "Ratings" },
    { url: "/property-ratings/add", path: "Ratings/AddEdit" },
    { url: "/property-ratings/edit/:id", path: "Ratings/AddEdit" },
    { url: "/property-ratings/detail/:id", path: "Ratings/View" },
    { url: "/property-highlights", path: "ValorizationItem" },
    { url: "/property-highlights/add", path: "ValorizationItem/AddEdit" },
    { url: "/property-highlights/edit/:id", path: "ValorizationItem/AddEdit" },

    { url: "/property-quick-search", path: "QuickSearch" },
    { url: "/property-quick-search/add", path: "QuickSearch/AddEdit" },
    { url: "/property-quick-search/edit/:id", path: "QuickSearch/AddEdit" },
    { url: "/property-quick-search/detail/:id", path: "QuickSearch/View" },
    { url: "/property-external-link", path: "PropertyExternalLink" },
    { url: "/property-individual-owners", path: "PropertyIndividualOwners" },
    { url: "/plan", path: "Plans" },
    { url: "/plan/edit/:id", path: "Plans/AddEdit" },
    { url: "/plan/add", path: "Plans/AddEdit" },
    { url: "/plan/detail/:id", path: "Plans/View" },
    { url: "/plan/tva", path: "Plans/Tva" },
    { url: "/plan/sales", path: "Plans/SubscriptionSales" },
    { url: "/plan/subscribers", path: "Plans/Subscribers" },
    { url: "/plan/unpaid", path: "Plans/Unpaid" },
    { url: "/plan/attrition", path: "Plans/Attrition" },
    { url: "/plan-feature", path: "Features" },
    { url: "/plan-feature/detail/:id", path: "Features/View" },
    { url: "/enquiry", path: "Enquiry" },
    { url: "/enquiry/detail/:id", path: "Enquiry/View" },
    { url: "/user-requests", path: "UserRequests" },
    { url: "/property-attractivity/profile-activity", path: "PropertyAttractivity/ProfileActivity" },
    { url: "/property-attractivity/attractivity-index", path: "PropertyAttractivity/AttractivityIndex" },
    { url: "/review", path: "Review" },
    { url: "/service", path: "Service" },
    { url: "/service/add", path: "Service/AddEdit" },
    { url: "/service/edit/:id", path: "Service/AddEdit" },
    { url: "/faq", path: "Faq" }, // FAQ Module Routes
    { url: "/faq/edit/:id", path: "Faq/AddEdit" },
    { url: "/faq/add", path: "Faq/AddEdit" },
    { url: "/faq/detail/:id", path: "Faq/View" },

    { url: "/presetSearch", path: "PresetSearches" }, // Staff Module Routes
    { url: "/presetSearch/edit/:id", path: "PresetSearches/AddEdit" },
    { url: "/presetSearch/add", path: "PresetSearches/AddEdit" },
    { url: "/presetSearch/detail/:id", path: "PresetSearches/View" },

    { url: "/review-company", path: "CompanyReview" }, // Staff Module Routes
    { url: "/review-company/edit/:id", path: "CompanyReview/AddEdit" },
    { url: "/review-company/add", path: "CompanyReview/AddEdit" },
    { url: "/review-company/detail/:id", path: "CompanyReview/View" },

    { url: "/p2p-estimation", path: "P2pEstimation" }, // Staff Module Routes
    { url: "/p2p-estimation/edit/:id", path: "P2pEstimation/AddEdit" },
    { url: "/p2p-estimation/add", path: "P2pEstimation/AddEdit" },
    { url: "/p2p-estimation/detail/:id", path: "P2pEstimation/View" },
    { url: "/p2p-estimation/campaigns", path: "P2pEstimation/Campaigns" },
    { url: "/p2p-estimation/campaigns/:id", path: "P2pEstimation/CampaignDetail" },
    { url: "/p2p-estimation/price-per-sqm", path: "P2pEstimation/PricePerSqm" },

    { url: "/admin-setting", path: "AdminSetting" },
    { url: "/admin-setting/edit/:id", path: "AdminSetting/AddEdit" },
    { url: "/admin-setting/detail/:id", path: "AdminSetting/View" },
    { url: "/dream-bigger-settings", path: "DreamBiggerSettings" },
    { url: "/dream-bigger-projections", path: "DreamBigger/Projections" },
    { url: "/social-proof", path: "SocialProof" },

    { url: "/property-claim-ownership", path: "ClaimOwnerShip" },
    { url: "/property-claim-ownership/detail/:id", path: "ClaimOwnerShip/View" },

    { url: "/training-topic", path: "TrainingTopic" },
    { url: "/training-topic/add", path: "TrainingTopic/AddEdit" },
    { url: "/training-topic/edit/:id", path: "TrainingTopic/AddEdit" },
    { url: "/training-topic/detail/:id", path: "TrainingTopic/View" },

    { url: "/qr-code-stats", path: "QRCodeStats" },
    { url: "/referral", path: "ReferralAdmin" },
    { url: "/referral-program", path: "ReferralProgramAdmin" },
    { url: "/learning-content", path: "LearningContentAdmin" },
    { url: "/learning-center-content", path: "LearningCenterContent" },
    { url: "/moteur-immo", path: "MoteurImmoImport" },
    { url: "/moteur-immo/detail/:id", path: "MoteurImmoImport/View" },
    { url: "/moteur-immo-dashboard", path: "MoteurImmoDashboard" },
    { url: "/moteur-immo-runs", path: "MoteurImmoRuns" },
    { url: "/agency-reveal", path: "AgencyReveal" },
    { url: "/building-permis", path: "BuildingPermitAdmin" },
    { url: "/llm-monitoring", path: "LlmMonitoring" },
    // Estimations AVM (back-office)
    { url: "/estimations", path: "Estimations" },
    { url: "/repere-projections", path: "RepereProjections" },
    { url: "/repere-projections/parametres", path: "RepereProjections/Settings" },
    // Agent autonome (back-office)
    { url: "/autonomous-agents", path: "AutonomousAgents/Dashboard" },
    { url: "/autonomous-agents/agents", path: "AutonomousAgents/Agents" },
    { url: "/autonomous-agents/conversations", path: "AutonomousAgents/Conversations" },
    { url: "/autonomous-agents/leads", path: "AutonomousAgents/Leads" },
    { url: "/autonomous-agents/documents", path: "AutonomousAgents/Documents" },
    { url: "/autonomous-agents/escalations", path: "AutonomousAgents/Escalations" },
    { url: "/autonomous-agents/incidents", path: "AutonomousAgents/Incidents" },
    { url: "/autonomous-agents/usage", path: "AutonomousAgents/Usage" },
    { url: "/autonomous-agents/settings", path: "AutonomousAgents/Settings" },
    { url: "/dpe-analysis", path: "DpeAnalysis" },
    { url: "/dpe-analysis/usage", path: "DpeAnalysis" },
    { url: "/homestaging", path: "Homestaging" },
    { url: "/homestaging/usage", path: "Homestaging" },
    // Assistant Immo IA Candidat (back-office)
    { url: "/assistant-candidat", path: "AssistantCandidat/Dashboard" },
    { url: "/assistant-candidat/agents", path: "AssistantCandidat/Agents" },
    { url: "/assistant-candidat/searches", path: "AssistantCandidat/Searches" },
    { url: "/assistant-candidat/matching", path: "AssistantCandidat/Matching" },
    { url: "/assistant-candidat/reports", path: "AssistantCandidat/Reports" },
    { url: "/assistant-candidat/conversations", path: "AssistantCandidat/Conversations" },
    { url: "/assistant-candidat/visits", path: "AssistantCandidat/Visits" },
{ url: "/assistant-candidat/incidents", path: "AssistantCandidat/Incidents" },
   { url: "/assistant-candidat/document-consultations", path: "AssistantCandidat/DocumentConsultations" },
   { url: "/assistant-candidat/usage", path: "AssistantCandidat/Usage" },
    { url: "/assistant-candidat/settings", path: "AssistantCandidat/Settings" },
    // Marque Blanche
    { url: "/white-label", path: "WhiteLabel" },
    { url: "/white-label/users", path: "WhiteLabel/Users" },
    { url: "/white-label/stats", path: "WhiteLabel/Stats" },
    { url: "/white-label/plans", path: "WhiteLabel/Plans" },
  ];

  sessionStorage.clear();

  return (
    <>
      <Provider store={store}>
        <PersistGate loading={"loading ..."} persistor={persistor}>
          <Suspense
            fallback={
              <div id="loader" className="loaderDiv">
                <div>
                  <img
                    src="/assets/img/loader.gif"
                    alt="logo"
                    className="loaderlogo"
                  />
                </div>
              </div>
            }
          >
            <Router>
              <PageTitle routes={routes} />
              <Routes>
                {routes.map((itm, index) => {
                  const Element = lazy(() => import(`./Pages/${itm.path}`));
                  return (
                    <Route
                      path={itm.url}
                      key={index}
                      element={itm.path ? <Element /> : itm.element}
                    />
                  );
                })}
              </Routes>
            </Router>
          </Suspense>
        </PersistGate>
      </Provider>
      <div id="loader" className="loaderDiv d-none">
        <div>
          <img src="/assets/img/loader.gif" alt="logo" className="loaderlogo" />
        </div>
      </div>
      <ToastContainer position="top-right" className="toasterDiv" />
    </>
  );
}

export default App;
